/** Per-file upload progress with status + retry. */
import { motion, AnimatePresence } from 'framer-motion';
import { IconCheck, IconX, IconReload } from '@tabler/icons-react';
import type { UploadTask, UploadStatus } from '../../hooks/useUploadQueue.ts';
import { formatSize } from '../../lib/media.ts';
import { cn } from '../../lib/cn.ts';

const STATUS_LABEL: Record<UploadStatus, string> = {
  pending: 'Waiting…',
  reading: 'Reading metadata…',
  compressing: 'Compressing…',
  uploading: 'Uploading…',
  thumbnail: 'Finishing…',
  done: 'Done',
  error: 'Failed',
};

interface Props {
  tasks: UploadTask[];
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
}

export function UploadProgressList({ tasks, onRetry, onRemove }: Props) {
  if (tasks.length === 0) return null;

  return (
    <ul className="space-y-2">
      <AnimatePresence initial={false}>
        {tasks.map((task) => (
          <motion.li
            key={task.id}
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl border border-line bg-surface shadow-sm p-3"
          >
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-fg">
                    {task.file.name}
                  </span>
                  <span className="shrink-0 text-xs text-muted">{formatSize(task.file.size)}</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-grid">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-300',
                      task.status === 'error'
                        ? 'bg-rose-500'
                        : task.status === 'done'
                          ? 'bg-emerald-500'
                          : 'bg-accent',
                    )}
                    style={{ width: `${task.progress}%` }}
                  />
                </div>
                <p
                  className={cn(
                    'mt-1 text-xs',
                    task.status === 'error' ? 'text-rose-400' : 'text-muted',
                  )}
                >
                  {task.status === 'error' && task.error ? task.error : STATUS_LABEL[task.status]}
                </p>
              </div>

              <div className="shrink-0">
                {task.status === 'done' && <IconCheck className="h-5 w-5 text-emerald-500" />}
                {task.status === 'error' && (
                  <button
                    onClick={() => onRetry(task.id)}
                    className="inline-flex items-center gap-1 rounded-lg bg-accent/90 px-2 py-1 text-xs font-medium text-white hover:bg-accent"
                  >
                    <IconReload className="h-3.5 w-3.5" /> Retry
                  </button>
                )}
                {(task.status === 'pending' || task.status === 'done' || task.status === 'error') && (
                  <button
                    onClick={() => onRemove(task.id)}
                    className="ml-1 rounded-lg p-1 text-muted hover:bg-grid"
                    aria-label={`Remove ${task.file.name}`}
                  >
                    <IconX className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  );
}
